/**
 * Stats strip. A compact readout above the export actions:
 * Points / Columns / Size / Missing on the Export tab, and Load / Missing on the
 * Plot tab. The export-tab figures come from the shared `lastEstimate` atom (the
 * §5 debounced /estimate engine); the Missing % is derived locally from the
 * picked columns' schema null-%. A spinner shows while an estimate is in flight.
 *
 * Per-tab visibility is the blob's `data-tab` attribute, gated by the shell's
 * `body[data-active-tab]` CSS. The plot-tab figures (Load time, plot Missing)
 * stay "-" until the plot area (§10.8) lands and writes its own atoms.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import {
  lastEstimate,
  estimatePending,
  selectedCols,
  allColumns,
} from '../../state/store.ts';
import { formatBytes } from '../../utils/fmt.ts';
import { shipLabel } from '../../utils/ships.ts';
import {
  lastPlotData,
  plotLoadStat,
  plotMissingStat,
  plotPointStat,
} from '../plot/plot-ui-state.ts';
import { exportMissingStat } from '../../utils/columns.ts';

@customElement('export-stats')
export class ExportStats extends LightElement {
  private store = new StoreController(this, [
    lastEstimate,
    estimatePending,
    selectedCols,
    allColumns,
    plotPointStat,
    plotLoadStat,
    plotMissingStat,
    lastPlotData,
  ]);

  override render(): TemplateResult {
    const est = lastEstimate.value;
    const pending = estimatePending.value;
    const missing = exportMissingStat(allColumns.value, selectedCols.value);

    const points = est ? est.row_count.toLocaleString() : '-';
    const cols = est ? est.column_count.toLocaleString() : '-';
    const size = est ? formatBytes(est.estimated_bytes) : '-';
    const missingText = missing ? `${missing.avg.toFixed(1)}%` : '-';
    const missingTitle = missing
      ? missing.perCol.length
        ? `Per-column null % (from schema):\n${missing.perCol.join('\n')}`
        : 'No nulls detected'
      : '';

    // Plot-tab figures. `Points` here is the drawn plot's own count, not the
    // export estimate - see `plotPointStat`.
    const pts = plotPointStat.value;
    const load = plotLoadStat.value;
    const pmiss = plotMissingStat.value;
    // A heatmap and a correlation matrix draw cells, not points - the label
    // follows the drawn chart so the figure and its name agree.
    const drawnType = lastPlotData.value?.plot_type;
    const isHeat = drawnType === 'heatmap' || drawnType === 'correlation';
    const isPie = drawnType === 'pie';
    const isBox = drawnType === 'box';
    const isTable = drawnType === 'table';
    const plotPointsLabel = isHeat ? 'Cells:' : isPie ? 'Slices:' : isBox ? 'Boxes:' : isTable ? 'Rows:' : 'Points:';
    const plotPoints = pts ? `${pts.drawn.toLocaleString()}${pts.partial ? ' partial' : ''}` : '-';
    const plotPointsTitle = pts
      ? [
          ...(pts.partial ? ['Partial result: one or more panels failed.'] : []),
          isHeat
            ? `${pts.scanned.toLocaleString()} rows read, binned into ${pts.drawn.toLocaleString()} occupied cells`
            : isPie
            ? `${pts.scanned.toLocaleString()} rows read, shared out into ${pts.drawn.toLocaleString()} slices`
            : isBox
            ? `${pts.scanned.toLocaleString()} rows read, summarised into ${pts.drawn.toLocaleString()} boxes`
            : isTable
            ? `${pts.scanned.toLocaleString()} rows read, summarised into ${pts.drawn.toLocaleString()} table rows`
            : `${pts.scanned.toLocaleString()} rows read, downsampled to ${pts.drawn.toLocaleString()} points`,
          ...pts.perShip.map(
            (p) =>
              `${shipLabel(p.ship)}: ${p.drawn.toLocaleString()} of ${p.scanned.toLocaleString()}`,
          ),
        ].join('\n')
      : '';
    const loadText = load ? `${(load.ms / 1000).toFixed(1)}s` : '-';
    const plotMissingText = pmiss ? `${pmiss.pct.toFixed(1)}%` : '-';

    return html`
      <div class="stats-strip ${pending ? 'loading' : ''}">
        <span class="stat-item" data-tab="export">
          <span class="stat-label">Points:</span>
          <span class="stat-value">${points}</span>
        </span>
        <span class="stat-item" data-tab="export">
          <span class="stat-label">Columns:</span>
          <span class="stat-value">${cols}</span>
        </span>
        <span class="stat-item" data-tab="export">
          <span class="stat-label">Size:</span>
          <span class="stat-value">${size}</span>
        </span>
        <span class="stat-item" data-tab="export">
          <span class="stat-label">Missing:</span>
          <span class="stat-value" title=${missingTitle}>${missingText}</span>
        </span>
        <span class="stat-item" data-tab="plot">
          <span class="stat-label">${plotPointsLabel}</span>
          <span class="stat-value" title=${plotPointsTitle}>${plotPoints}</span>
        </span>
        <span class="stat-item" data-tab="plot">
          <span class="stat-label">Load:</span>
          <span class="stat-value">${loadText}</span>
        </span>
        <span class="stat-item" data-tab="plot">
          <span class="stat-label">Missing:</span>
          <span class="stat-value" title=${pmiss?.title ?? ''}>${plotMissingText}</span>
        </span>
        ${pending
          ? html`<span class="stats-spinner"><span class="spinner"></span></span>`
          : nothing}
      </div>
      ${est?.warning
        ? html`<div class="warning-box visible">${est.warning}</div>`
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'export-stats': ExportStats;
  }
}
